import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CompteARebourd from '../CompteARebourd/CompteARebourd';
import './SuiviHeader.css';
import { getStatusColor, formatStatusName } from '../../utils/entityUtils';
import { estNumeroPanier } from '../../utils/numeroPanier';
import { libelleStatut } from '../../utils/statusLabel';

// Statuts BC affichant le chrono : les fenetres ou le prestataire doit agir avant l'echeance.
// Un meme moment du workflow porte DEUX statuts, d'ou les six valeurs — la case prestataire passe
// le statut de sa COMMANDE, la case acheteur celui de l'OPERATION :
//
//   moment                | commande (presta) | operation (acheteur)
//   rang 1 "A accepter"   |         4         |          3
//   rang 2 "A accepter"   |         4         |          5
//   rang 3 "A accepter"   |         4         |          6
//   "A chiffrer"          |        11         |         10
//
// Passer par le statut, et non par la commande engagee, garantit que chacun ne voit que SON echeance :
// le statut porte le rang, donc un rang 2/3 ne peut pas heriter du compte a rebours du rang 1.
// Exclut 12 "A relancer" (le delai y est justement ecoule) et les statuts terminaux (7/8/9/14).
//
// La condition ne testait que le 11, d'ou la disparition : le 4 est devenu le cas courant depuis que
// le descriptif financier propose plusieurs voies (seul "je laisse le titulaire chiffrer" mene au 11),
// et aucune OPERATION ne porte le 11 — le chrono acheteur etait donc structurellement invisible.
const BC_STATUTS_AVEC_CHRONO = [3, 4, 5, 6, 10, 11];

interface SuiviHeaderProps {
  titre: string;
  sousTitre: string;
  // Nom de l'ouvrage (MS) : quand fourni, il devient le titre principal (h1) et `titre` (nom de
  // l'operation) passe en sous-ligne (h2). Absent -> comportement historique (h1=titre, h2=sousTitre).
  nomOuvrage?: string;
  subtitle: string;
  status: string;
  statusColor: string;
  delais?: string;
  backgroundImage?: string;
  noNavigation?: boolean;
  statusId?: number;
  source?: string; // BC, MS, ou BCI (Paniers importés)
  onRetour?: () => void;
  hideStatusBadge?: boolean; // mode démo : masque le badge statut, affiche uniquement le chrono
  depotLabel?: string; // texte statique avant le chrono (ex: "Fin de dépôt : 31/12/2050 à 12h00 heure de Paris — Temps restant :")
  retourLabel?: string; // texte du bouton retour (defaut "Retour vers les commandes" ; candidatures -> "Retour vers les candidatures")
  // Encart orange (a gauche) des suivis de COMMANDE (BC + MS, acheteur & prestataire) : statut en haut
  // (chip blanc, texte colore par statut), puis n° de panier (numero initial), sensibilite (MS), n° de
  // marche subsequent / bon de commande. Le statut reutilise le badge interne (currentStatus/couleur) ;
  // le libelle marche vient de `source`. Absent -> pas d'encart.
  encart?: {
    // id de l'operation : AFFICHE tel quel comme "n° de panier" (juste l'id) ; sert aussi a reconstruire
    // le numero initial "PMS/PBC + id" pour detecter si le n° de marche a ete transforme.
    operationId?: number | string | null;
    numeroMarche: string;
    sensibilite?: string | null;
  };
}

const SuiviHeader: React.FC<SuiviHeaderProps> = ({
  titre,
  sousTitre,
  nomOuvrage,
  subtitle,
  status,
  statusColor,
  delais,
  backgroundImage,
  noNavigation,
  statusId,
  source,
  onRetour,
  hideStatusBadge = false,
  depotLabel,
  retourLabel = 'Retour vers les commandes',
  encart,}) => {
  const navigate = !noNavigation ? useNavigate() : null;

  // NOUVEAU: Fonction pour vérifier si la date est déjà expirée
  const isDateExpired = (dateStr: string): boolean => {
    if (!dateStr) return false;
    const targetDate = new Date(dateStr);
    if (isNaN(targetDate.getTime())) return false;
    return Date.now() >= targetDate.getTime();
  };

  // NOUVEAU: Calculer si expiré par statut (en dehors pour éviter la recréation)
  const checkExpiredByStatus = (src?: string, stId?: number, delaisValue?: string): boolean => {
    // Ne considérer comme expiré que si on a une date d'expiration
    if (!delaisValue) return false;
    // CORRECTION: Exclure les BCI (source === 'BCI') de la logique d'expiration
    if (src === 'BCI') return false;
    return (src === 'MS' && stId === 12) || (src === 'BC' && stId === 9);
  };

  // Expiration par DATE : ne vaut que dans la fenetre d'attente de reponse -> BC (comportement
  // inchange) et MS UNIQUEMENT en consultation (statut 2). Au-dela, le STATUT fait foi : un MS deja
  // signe (11) ou avance, dont la date de reponse initiale est passee, n'est PAS expire. BCI exclu.
  const isExpiredByDate = (src?: string, stId?: number, delaisValue?: string): boolean => {
    if (!delaisValue || !isDateExpired(delaisValue)) return false;
    // MS cote PRESTA (seul consommateur de SuiviHeader en source="MS") : la fenetre de reponse datee
    // est "A candidater" (statut 3). Le statut 2 (consultation acheteur) reste couvert par securite.
    return src === 'BC' || (src === 'MS' && (stId === 2 || stId === 3));
  };

  // NOUVEAU: Déterminer le statut initial en fonction de la date ou du statut
  const getInitialStatus = () => {
    if (checkExpiredByStatus(source, statusId, delais) || isExpiredByDate(source, statusId, delais)) {
      return 'Expiré';
    }
    return status;
  };

  const getInitialStatusColor = () => {
    if (checkExpiredByStatus(source, statusId, delais) || isExpiredByDate(source, statusId, delais)) {
      return getStatusColor('Expiré');
    }
    return getStatusColor(statusColor);
  };

  const [currentStatus, setCurrentStatus] = useState(getInitialStatus());
  const [currentStatusColor, setCurrentStatusColor] = useState(getInitialStatusColor());
  const [isExpired, setIsExpired] = useState(checkExpiredByStatus(source, statusId, delais) || isExpiredByDate(source, statusId, delais));

  // NOUVEAU: Écouter l'événement d'expiration du chrono
  useEffect(() => {
    const handleChronoExpired = () => {
      setCurrentStatus('Expiré');
      setCurrentStatusColor(getStatusColor('Expiré'));
      setIsExpired(true);
    };

    window.addEventListener('chronoExpired', handleChronoExpired);

    return () => {
      window.removeEventListener('chronoExpired', handleChronoExpired);
    };
  }, []);

  // Mettre à jour le statut si les props changent
  useEffect(() => {
    const expiredByStatus = checkExpiredByStatus(source, statusId, delais);
    const expiredByDate = isExpiredByDate(source, statusId, delais);



    if (expiredByStatus || expiredByDate) {
      setCurrentStatus('Expiré');
      setCurrentStatusColor(getStatusColor('Expiré'));
      setIsExpired(true);
    } else {
      setCurrentStatus(status);
      setCurrentStatusColor(statusColor);
      setIsExpired(false);
    }
  }, [status, statusColor, delais, statusId, source]);

  // Fonction pour normaliser le statut en classe CSS
  const getStatusClassName = (statusValue: string | null | undefined): string => {
    if (!statusValue) return 'status-unknown';

    return `status-${statusValue
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // supprime les accents
      .replace(/[^a-z0-9]+/g, '-')     // remplace tout ce qui n'est pas lettre/chiffre par un tiret
      .replace(/^-+|-+$/g, '')         // supprime les tirets en début/fin
      }`;
  };

  // Libelle du statut, IDENTIQUE a celui de la ligne cliquee dans la liste (table utils/statusLabel,
  // partagee avec les deux tableaux). "Expiré" prime : il est calcule ici par date/statut et ne
  // correspond pas toujours au statut porte par l'entite.
  const statutAffiche =
    currentStatus === 'Expiré'
      ? formatStatusName(currentStatus)
      : formatStatusName(libelleStatut(source, statusId, currentStatus));

  // Chrono (compte a rebours) : place EN BAS de l'encart orange quand il existe, sinon a droite
  // (consommateurs SuiviHeader sans encart : candidatures, etc.).
  // MS cote PRESTA : le chrono s'affiche pendant la fenetre de reponse = statut 3 "A candidater"
  // (et 2 par securite). `delais` = expire_at de la consultation.
  // 'deposee' (candidature AC) : tant que la date limite de depot court, le
  // prestataire peut encore annuler sa candidature et en redeposer une. Le chrono
  // lui dit combien de temps il lui reste pour le faire. Les statuts posterieurs
  // (en_analyse, retenue, classee...) sont de toute facon au-dela de l'echeance,
  // et !isExpired les ecarte.
  const chronoEl = delais && !isExpired && (hideStatusBadge || status === 'brouillon' || status === 'deposee' || status === 'a_signer' || (source === 'MS' && (statusId === 2 || statusId === 3)) || (source === 'BC' && statusId !== undefined && BC_STATUTS_AVEC_CHRONO.includes(statusId))) ? (
    <span className="countdown-container">
      {depotLabel && (
        <span style={{ fontWeight: 500, marginRight: '0.4rem', whiteSpace: 'nowrap' }}>
          {depotLabel}
        </span>
      )}
      <CompteARebourd targetDate={delais} source={source} />
    </span>
  ) : null;

  // Titre principal = nom de l'ouvrage si fourni, sinon le nom de l'operation (`titre`).
  // Sous-ligne = le nom de l'operation quand l'ouvrage occupe la ligne 1 ; sinon le `sousTitre` historique.
  const primaryTitle = nomOuvrage || titre;
  const secondaryTitle = nomOuvrage ? titre : sousTitre;

  const bodyContent = (
    <>
      <h1 title={primaryTitle}>{primaryTitle}</h1>
      {secondaryTitle && <h2>{secondaryTitle}</h2>}

      <div className="subtitle">
        <span>{subtitle}</span>
      </div>

      {/* Sensibilite (MS) : deplacee de l'encart vers le bloc de droite, sur une ligne. */}
      {encart?.sensibilite != null && encart.sensibilite !== '' && (
        <div className="suivi-header__sensibilite">Sensibilité : {encart.sensibilite}</div>
      )}

      {/* Bloc statut de droite : rendu UNIQUEMENT hors encart (pages sans encart : candidatures...).
          Avec encart, la pastille est desactivee ET le chrono vit dans l'encart -> ce bloc ne serait
          qu'une pilule blanche vide (.status-header a un fond blanc global) -> on ne le rend pas. */}
      {!encart && (
        <div className="status-container">
          <div className="status-header">
            {/* Pastille de statut DESACTIVEE ; gardee via `false &&` pour reactivation facile. */}
            {false && !hideStatusBadge && (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 45 41" fill="none">
                  <ellipse cx="22.5" cy="20.5" rx="22.5" ry="20.5" fill={currentStatusColor} />
                </svg>
                <span className={`status-text ${getStatusClassName(currentStatus)}`} style={{ color: currentStatusColor }}>
                  {statutAffiche}
                </span>
              </>
            )}
            {chronoEl}
          </div>
        </div>
      )}
    </>
  );

  // Encart orange (a gauche) des suivis de commande : statut (chip blanc + texte colore, MEME libelle
  // et MEME couleur que le badge) + n° panier + sensibilite (MS) + n° marche subsequent / bon de commande.
  const encartStatut = statutAffiche;
  const encartMarcheLabel = source === 'MS' ? 'N° de marché subséquent' : 'N° de bon de commande';
  // Affichage n° de panier = juste l'id de l'operation (pas le "PMS00XXX").
  const encartPanierAffiche =
    encart && encart.operationId != null && encart.operationId !== '' ? String(encart.operationId) : '-';
  // Le numero de marche n'existe qu'a l'acceptation. Tant qu'il n'est pas attribue, `operation.numero`
  // porte encore un numero de PANIER : on affiche donc "Inconnu à ce stade" plutot que ce numero-la.
  //
  // Un vrai numero s'ecrit `BC-...` / `MS-...`. Tant que `operation.numero` porte encore un numero de
  // panier (`PBC02038` ou sa variante sans "P", `BC00165`), il n'y a pas de numero de marche a montrer.
  const encartMarcheValeur =
    encart && !estNumeroPanier(encart.numeroMarche) ? encart.numeroMarche : 'Inconnu à ce stade';

  const encartBox = encart ? (
    <aside className="suivi-header__encart no-print">
      {/* Statut : chip blanc, texte colore par statut (plus de libelle "Statut :"), tient sur une ligne. */}
      <div className="suivi-header__encart-statut" style={{ color: currentStatusColor }}>{encartStatut}</div>
      <div className="suivi-header__encart-ligne">
        <span className="suivi-header__encart-lbl">N° de panier : </span>
        <span className="suivi-header__encart-val">{encartPanierAffiche}</span>
      </div>
      <div className="suivi-header__encart-ligne suivi-header__encart-ligne--bloc">
        <span className="suivi-header__encart-lbl">{encartMarcheLabel} :</span>
        <span className="suivi-header__encart-val">{encartMarcheValeur}</span>
      </div>
      {/* Chrono EN BAS de l'encart : blanc, sans encadrement, libelle "Temps restant :" au-dessus. */}
      {chronoEl && (
        <div className="suivi-header__encart-chrono">
          <span className="suivi-header__encart-chrono-lbl">Temps restant :</span>
          {chronoEl}
        </div>
      )}
    </aside>
  ) : null;

  return (
    <div
      className="suivi-commande-title"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${backgroundImage})`
      }}
    >
      {!noNavigation && navigate && (
        // Retour en TEXTE (style login) au lieu du chevron. Navigation inchangee (onRetour ?? -1).
        <button className="suivi-header__back no-print" onClick={() => onRetour ? onRetour() : navigate(-1)} aria-label={retourLabel}>
          ← {retourLabel}
        </button>
      )}

      {encartBox ? (
        <div className="suivi-header__row">
          {encartBox}
          <div className="suivi-header__col">{bodyContent}</div>
        </div>
      ) : bodyContent}
    </div>
  );
};

export default SuiviHeader;